import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  FileSpreadsheet,
  FileText,
  Plus,
  Search,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import excelIcon from "@/assets/excel-icon.png";
import pdfIcon from "@/assets/pdf-icon.png";
import wordIcon from "@/assets/word-icon.png";
import powerpointIcon from "@/assets/powerpoint-icon.png";

import { PROJECT_DOCUMENT_ACCEPT } from "@/lib/api/documents";
import { Switch } from "@/components/ui/switch";

export type DatabookUploadDocumentStatus =
  | "ready"
  | "uploading"
  | "deleting"
  | "error";

export interface DatabookUploadDocument {
  key: string;
  name: string;
  size: number;
  status: DatabookUploadDocumentStatus;
  isSupported?: boolean;
  canDownload?: boolean;
  canRetry?: boolean;
}

interface DatabookUploadStageProps {
  documents: DatabookUploadDocument[];
  documentsLoading?: boolean;
  documentsLoadError?: boolean;
  canUpload?: boolean;
  uploadPrompt?: string;
  dealContext: string;
  dealContextDisabled?: boolean;
  onDealContextChange: (value: string) => void;
  onSaveDealContext: () => boolean | void | Promise<boolean | void>;
  financialYearEnd: string;
  financialYearEndDisabled?: boolean;
  onFinancialYearEndChange: (value: string) => void;
  periodOptions: readonly string[];
  scopeFrom: string;
  scopeTo: string;
  includeYtd?: boolean;
  scopeDisabled?: boolean;
  onScopeFromChange: (value: string) => void;
  onScopeToChange: (value: string) => void;
  onIncludeYtdChange?: (value: boolean) => void;
  includeForecast?: boolean;
  forecastYears?: string;
  forecastDisabled?: boolean;
  onIncludeForecastChange?: (value: boolean) => void;
  onForecastYearsChange?: (value: string) => void;
  canClearDocuments?: boolean;
  canRemoveDocuments?: boolean;
  onFilesSelected: (files: FileList) => void;
  onClearDocuments: () => void;
  onRemoveDocument: (document: DatabookUploadDocument) => void;
  onDownloadDocument?: (document: DatabookUploadDocument) => void;
  onRetryDocument?: (document: DatabookUploadDocument) => void;
  continueLabel: string;
  continueDisabled?: boolean;
  /** Source analysis is under way — show the how-long-it-takes notice. */
  analysisRunning?: boolean;
  onRetryAnalysis?: () => void;
  onContinue: () => void;
}

const UI_FONT = {
  fontFamily:
    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const FINANCIAL_YEAR_END_OPTIONS = [
  "31 January",
  "28 February",
  "31 March",
  "30 April",
  "31 May",
  "30 June",
  "31 July",
  "31 August",
  "30 September",
  "31 October",
  "30 November",
  "31 December",
] as const;

const FORECAST_YEAR_OPTIONS = [
  "1 year",
  "2 years",
  "3 years",
  "4 years",
  "5 years",
] as const;

const UPLOAD_GUIDE = [
  [
    "Financials and ledgers",
    [
      "Trial balances, every entity and period",
      "Monthly management accounts, P&L and balance sheet",
      "Statutory and annual accounts",
      "General / nominal ledger detail",
      "Sales ledger and aged debtors",
      "Purchase ledger and aged creditors",
      "Bank statements and reconciliations",
      "Cash flow statements",
    ],
  ],
  [
    "P&L detail",
    [
      "Revenue by customer, product and channel",
      "Cost of sales breakdown",
      "Gross margin by product / SKU",
      "Overheads and opex breakdown",
      "Payroll reports, by month with headcount and FTE",
      "Bonus and commission schedules",
      "Accruals breakdown",
      "Prepayments breakdown",
      "Provisions and impairment schedules",
      "One-off / exceptional items listing",
    ],
  ],
  [
    "Balance sheet detail",
    [
      "Fixed asset register, additions and disposals",
      "Inventory / stock listings and ageing",
      "Work in progress schedule",
      "Deferred revenue schedule",
      "Intercompany balances and eliminations",
      "Debt schedule and loan agreements",
      "Lease schedule (IFRS 16)",
    ],
  ],
  [
    "Deal-related materials",
    [
      "Information memorandum (IM), PDF",
      "Term sheet",
      "Annual report, PDF",
      "Board packs",
      "Budgets",
      "Forecast / financial model",
      "Capex plans",
      "Customer and supplier contracts",
      "Group structure / org chart",
      "Accounting policies",
    ],
  ],
  [
    "Correspondence and Q&A",
    [
      "RFI responses, with management comments",
      "Meeting notes and emails",
      "Management call notes",
    ],
  ],
] as const;

const FORMAT_ICONS = {
  xlsx: excelIcon,
  csv: null,
  pdf: pdfIcon,
  docx: wordIcon,
  pptx: powerpointIcon,
  other: null,
};

function formatOf(name: string) {
  const extension = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";
  if (["xls", "xlsx", "xlsm"].includes(extension)) return "xlsx";
  if (["csv", "tsv"].includes(extension)) return "csv";
  if (extension === "pdf") return "pdf";
  if (["doc", "docx"].includes(extension)) return "docx";
  if (
    [
      "ppt",
      "pptx",
      "pptm",
      "pps",
      "ppsx",
      "ppsm",
      "pot",
      "potx",
      "potm",
    ].includes(extension)
  ) return "pptx";
  return "other";
}

function formatSize(size: number) {
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024)).toLocaleString("en-GB")} KB`;
  }
  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function documentStatusLabel(status: DatabookUploadDocumentStatus) {
  if (status === "uploading") return "Uploading…";
  if (status === "deleting") return "Deleting…";
  if (status === "error") return "Upload failed";
  return null;
}

interface MenuSelectProps {
  label: string;
  value: string;
  options: readonly string[];
  disabled?: boolean;
  onChange: (value: string) => void;
}

function MenuSelect({
  label,
  value,
  options,
  disabled = false,
  onChange,
}: MenuSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`flex min-w-40 items-center justify-between rounded-lg border bg-card px-3 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-60 ${
          open
            ? "border-foreground/50 ring-2 ring-foreground/10"
            : "border-border hover:border-muted-foreground/50"
        }`}
        style={{ fontSize: 12 }}
      >
        <span>{value}</span>
        {open
          ? <ChevronUp size={14} className="text-foreground" />
          : <ChevronDown size={14} className="text-muted-foreground" />}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={`${label} options`}
          className="absolute left-0 top-full z-50 mt-1.5 max-h-80 min-w-52 overflow-y-auto rounded-xl border border-border bg-card p-1.5 shadow-xl"
        >
          {options.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={value === option}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors ${
                value === option
                  ? "bg-muted font-medium text-foreground"
                  : "text-foreground hover:bg-muted/70"
              }`}
              style={{ fontSize: 12 }}
            >
              <span className="flex w-3.5 shrink-0 items-center justify-center">
                {value === option && <Check size={14} strokeWidth={2.25} />}
              </span>
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface FiscalYearSelectProps {
  label: string;
  value: string;
  options: readonly string[];
  disabled?: boolean;
  onChange: (value: string) => void;
}

function FiscalYearSelect({
  label,
  value,
  options,
  disabled = false,
  onChange,
}: FiscalYearSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showAllOlderYears, setShowAllOlderYears] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const normalizedQuery = query.trim().toLowerCase();
  const fiscalYears = useMemo(
    () => options.filter((period) => period.startsWith("FY")),
    [options],
  );
  const recentYears = fiscalYears.slice(-4);
  const olderYears = fiscalYears.slice(0, -4).reverse();
  const visibleOlderYears = showAllOlderYears
    ? olderYears
    : olderYears.slice(0, 4);
  const matchingPeriods = normalizedQuery
    ? options.filter((period) =>
        period.toLowerCase().includes(normalizedQuery))
    : null;

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selectYear = (year: string) => {
    onChange(year);
    setOpen(false);
    setQuery("");
  };

  const renderYear = (year: string) => (
    <button
      key={year}
      type="button"
      role="option"
      aria-selected={value === year}
      onClick={() => selectYear(year)}
      className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors ${
        value === year
          ? "bg-muted font-medium text-foreground"
          : "text-foreground hover:bg-muted/70"
      }`}
      style={{ fontSize: 12 }}
    >
      <span className="flex w-3.5 shrink-0 items-center justify-center">
        {value === year && <Check size={14} strokeWidth={2.25} />}
      </span>
      {year}
    </button>
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={`${label} fiscal year`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`flex min-w-32 items-center justify-between rounded-lg border bg-card px-3 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-60 ${
          open
            ? "border-foreground/50 ring-2 ring-foreground/10"
            : "border-border hover:border-muted-foreground/50"
        }`}
        style={{ fontSize: 12 }}
      >
        <span>{value}</span>
        {open
          ? <ChevronUp size={14} className="text-foreground" />
          : <ChevronDown size={14} className="text-muted-foreground" />}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={`${label} fiscal year options`}
          className="absolute left-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-border bg-card shadow-xl"
        >
          <div className="p-2">
            <div className="flex h-9 items-center gap-2 rounded-lg border border-foreground/50 px-2.5 ring-2 ring-foreground/10">
              <Search size={15} className="shrink-0 text-foreground/80" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search year..."
                className="h-full min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
                style={{ fontSize: 12 }}
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto px-1.5 pb-1.5">
            {matchingPeriods ? (
              matchingPeriods.length > 0
                ? matchingPeriods.map(renderYear)
                : (
                  <div className="px-3 py-5 text-center text-muted-foreground" style={{ fontSize: 12 }}>
                    No matching years
                  </div>
                )
            ) : (
              <>
                <div className="px-2.5 pb-1 pt-1 text-muted-foreground uppercase tracking-wide" style={{ fontSize: 9.5, fontWeight: 650 }}>
                  Recent
                </div>
                {recentYears.map(renderYear)}
                {visibleOlderYears.length > 0 && (
                  <>
                    <div className="mx-2 my-1 border-t border-border" />
                    <div className="px-2.5 pb-1 pt-1 text-muted-foreground uppercase tracking-wide" style={{ fontSize: 9.5, fontWeight: 650 }}>
                      Older years
                    </div>
                    {visibleOlderYears.map(renderYear)}
                  </>
                )}
              </>
            )}
          </div>

          {!normalizedQuery && olderYears.length > 4 && (
            <button
              type="button"
              onClick={() => setShowAllOlderYears((current) => !current)}
              className="flex w-full items-center justify-between border-t border-border px-3 py-2.5 text-left hover:bg-muted"
              style={{ fontSize: 12 }}
            >
              {showAllOlderYears ? "Show fewer years" : "Show older years..."}
              <ChevronRight
                size={14}
                className={`transition-transform ${showAllOlderYears ? "rotate-90" : ""}`}
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function scopeEndOptions(
  periodOptions: readonly string[],
  scopeFrom: string,
) {
  const startYear = Number(scopeFrom.match(/\d+/)?.[0] ?? 0);
  return periodOptions.filter((period) => {
    if (!period.startsWith("FY")) return false;
    const periodYear = Number(period.match(/\d+/)?.[0] ?? 0);
    return periodYear >= startYear;
  });
}

export function DatabookUploadStage({
  documents,
  documentsLoading = false,
  documentsLoadError = false,
  canUpload = true,
  uploadPrompt = "Drag files or folders here, or browse",
  dealContext,
  dealContextDisabled = false,
  onDealContextChange,
  onSaveDealContext,
  financialYearEnd,
  financialYearEndDisabled = false,
  onFinancialYearEndChange,
  periodOptions,
  scopeFrom,
  scopeTo,
  includeYtd = false,
  scopeDisabled = false,
  onScopeFromChange,
  onScopeToChange,
  onIncludeYtdChange = () => undefined,
  includeForecast = false,
  forecastYears = "2 years",
  forecastDisabled = false,
  onIncludeForecastChange = () => undefined,
  onForecastYearsChange = () => undefined,
  canClearDocuments = true,
  canRemoveDocuments = true,
  onFilesSelected,
  onClearDocuments,
  onRemoveDocument,
  onDownloadDocument,
  onRetryDocument,
  continueLabel,
  continueDisabled = false,
  analysisRunning = false,
  onRetryAnalysis,
  onContinue,
}: DatabookUploadStageProps) {
  const [query, setQuery] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [dealContextSaved, setDealContextSaved] = useState(false);
  const [dealContextSaving, setDealContextSaving] = useState(false);
  const uploadingDocuments = useMemo(
    () => documents.filter((document) => document.status === "uploading"),
    [documents],
  );
  const uploadsInProgress = uploadingDocuments.length > 0;
  const analysisFileCount = documents.filter(
    (document) => document.isSupported !== false && document.status === "ready",
  ).length;

  const totalSize = useMemo(
    () => documents.reduce((total, document) => total + document.size, 0),
    [documents],
  );
  const shownDocuments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return documents.filter(
      (document) => normalizedQuery === ""
        || document.name.toLowerCase().includes(normalizedQuery),
    );
  }, [documents, query]);
  const documentGroups = [
    { unsupported: false, documents: shownDocuments.filter((document) => document.isSupported !== false) },
    { unsupported: true, documents: shownDocuments.filter((document) => document.isSupported === false) },
  ];
  const scopeFromOptions = useMemo(
    () => periodOptions.filter((period) => period.startsWith("FY")),
    [periodOptions],
  );
  const scopeToOptions = useMemo(
    () => scopeEndOptions(periodOptions, scopeFrom),
    [periodOptions, scopeFrom],
  );
  const ytdLabel = `Include YTD${String(
    Number(scopeTo.match(/\d+/)?.[0] ?? 0) + 1,
  ).slice(-2)}`;

  const saveDealContext = async () => {
    if (!dealContext.trim() || dealContextDisabled || dealContextSaving) return;
    setDealContextSaving(true);
    try {
      const saved = await onSaveDealContext();
      setDealContextSaved(saved !== false);
    } finally {
      setDealContextSaving(false);
    }
  };

  const acceptFiles = (files: FileList | null) => {
    if (!files || files.length === 0 || !canUpload) return;
    onFilesSelected(files);
  };

  return (
    <div className="flex-1 overflow-y-auto" style={UI_FONT}>
      <div
        className="mx-auto"
        style={{ maxWidth: 880, padding: "24px 32px 40px" }}
      >
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="font-semibold" style={{ fontSize: 20 }}>
            Add the data room
          </div>
          <button
            type="button"
            disabled={continueDisabled}
            onClick={onContinue}
            className="rounded-lg bg-foreground text-background font-medium px-4 py-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: 12.5 }}
          >
            {continueLabel}
          </button>
        </div>

        <div className="mb-3 rounded-xl border border-border bg-card px-4 py-3 space-y-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="font-semibold" style={{ fontSize: 12, minWidth: 130 }}>
              Financial year-end
            </span>
            <MenuSelect
              label="Financial year-end"
              value={financialYearEnd}
              disabled={financialYearEndDisabled}
              options={FINANCIAL_YEAR_END_OPTIONS}
              onChange={onFinancialYearEndChange}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/50 pt-2.5">
            <span className="font-semibold" style={{ fontSize: 12, minWidth: 130 }}>
              Period in scope
            </span>
            <FiscalYearSelect
              label="From"
              value={scopeFrom}
              disabled={scopeDisabled}
              options={scopeFromOptions}
              onChange={onScopeFromChange}
            />
            <span className="text-muted-foreground" style={{ fontSize: 12 }}>to</span>
            <FiscalYearSelect
              label="To"
              value={scopeTo}
              disabled={scopeDisabled}
              options={scopeToOptions}
              onChange={onScopeToChange}
            />
            <div className="flex min-h-8 items-center gap-2">
              <Switch
                aria-label={ytdLabel}
                checked={includeYtd}
                disabled={scopeDisabled}
                onCheckedChange={onIncludeYtdChange}
                className="origin-left scale-75 data-[state=checked]:bg-foreground data-[state=unchecked]:bg-muted-foreground/30"
              />
              <span className="text-foreground/80" style={{ fontSize: 11.5 }}>
                {ytdLabel}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/50 pt-2.5">
            <span className="font-semibold" style={{ fontSize: 12, minWidth: 130 }}>
              Forecast
            </span>
            <div className="flex min-h-8 items-center gap-2">
              <Switch
                aria-label="Include forecast"
                checked={includeForecast}
                disabled={forecastDisabled}
                onCheckedChange={onIncludeForecastChange}
                className="origin-left scale-75 data-[state=checked]:bg-foreground data-[state=unchecked]:bg-muted-foreground/30"
              />
              <span className="text-foreground/80" style={{ fontSize: 11.5 }}>
                Include forecast
              </span>
            </div>
            {includeForecast && (
              <MenuSelect
                label="Forecast period"
                value={forecastYears}
                disabled={forecastDisabled}
                options={FORECAST_YEAR_OPTIONS}
                onChange={onForecastYearsChange}
              />
            )}
          </div>
          <div className="flex items-center gap-2 border-t border-border/50 pt-2.5">
            <span className="font-semibold" style={{ fontSize: 12 }}>Deal context</span>
            <span className="text-muted-foreground" style={{ fontSize: 10.5 }}>optional</span>
          </div>
          <div>
            <textarea
              value={dealContext}
              disabled={dealContextDisabled}
              rows={3}
              maxLength={16000}
              onChange={(event) => {
                onDealContextChange(event.target.value);
                setDealContextSaved(false);
              }}
              placeholder="Tell us what this transaction is — e.g. carve-out of the NZ manufacturing business, buyer is a PE fund, FY24 is the reference period, watch for related-party sales and a management bonus accrual to normalise. Anything relevant to the analysis."
              className="w-full resize-none bg-muted rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-foreground/30 disabled:opacity-60"
              style={{ fontSize: 12, lineHeight: 1.55 }}
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              <span className="text-muted-foreground" style={{ fontSize: 10.5 }}>
                Saved
              </span>
              <button
                type="button"
                disabled={
                  !dealContext.trim()
                  || dealContextDisabled
                  || dealContextSaved
                  || dealContextSaving
                }
                onClick={saveDealContext}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-medium ${
                  dealContextSaved
                    ? "border border-border text-muted-foreground"
                    : dealContext.trim() && !dealContextDisabled
                      ? "bg-foreground text-background hover:opacity-90"
                      : "border border-border text-muted-foreground/50 cursor-not-allowed"
                }`}
                style={{ fontSize: 11.5 }}
              >
                {dealContextSaved ? (
                  <>
                    <Check size={12} strokeWidth={3} /> Saved
                  </>
                ) : dealContextSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>

        <div className="mb-2">
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            className="inline-flex items-center gap-1.5 text-foreground/80 hover:text-foreground underline underline-offset-2"
            style={{ fontSize: 12 }}
          >
            <FileText size={13} /> What should I upload?
          </button>
        </div>

        {showGuide && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
            onClick={() => setShowGuide(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="What to upload"
              className="bg-background rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 p-5 border-b border-border/60 shrink-0">
                <div className="font-semibold" style={{ fontSize: 16 }}>
                  What to upload
                </div>
                <button
                  type="button"
                  aria-label="Close upload guide"
                  onClick={() => setShowGuide(false)}
                  className="text-muted-foreground hover:text-foreground p-1"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="overflow-y-auto p-5 space-y-4">
                {UPLOAD_GUIDE.map(([section, items]) => (
                  <div key={section}>
                    <div
                      className="text-muted-foreground uppercase tracking-wider font-semibold mb-2"
                      style={{ fontSize: 9.5 }}
                    >
                      {section}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {items.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <Check
                            size={12}
                            strokeWidth={3}
                            className="text-muted-foreground shrink-0 mt-0.5"
                          />
                          <span style={{ fontSize: 12, lineHeight: 1.4 }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div
                  className="text-muted-foreground pt-1"
                  style={{ fontSize: 11, lineHeight: 1.5 }}
                >
                  Not a checklist — drop whatever you have and anything unreadable
                  or out of scope is set aside automatically.
                </div>
              </div>
            </div>
          </div>
        )}

        <label
          className={`block border-2 border-dashed border-border rounded-xl bg-muted/30 transition-colors ${
            uploadsInProgress
              ? "cursor-wait"
              : canUpload
              ? "cursor-pointer hover:bg-muted/60 hover:border-muted-foreground/40"
              : "cursor-not-allowed opacity-60"
          }`}
          style={{ padding: "24px" }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (uploadsInProgress) return;
            acceptFiles(event.dataTransfer.files);
          }}
        >
          <input
            type="file"
            multiple
            accept={PROJECT_DOCUMENT_ACCEPT}
            disabled={!canUpload || uploadsInProgress}
            className="hidden"
            onChange={(event) => {
              acceptFiles(event.target.files);
              event.target.value = "";
            }}
          />
          {uploadsInProgress ? (
            <div
              className="flex flex-col items-center gap-2 text-center"
              aria-live="polite"
              aria-busy="true"
            >
              <div
                className="relative flex items-center justify-center rounded-full border border-border bg-card"
                style={{ width: 52, height: 52 }}
              >
                <span className="absolute inset-[-1px] animate-spin rounded-full border-2 border-transparent border-t-foreground/70" />
                <UploadCloud size={20} className="text-foreground/80" />
              </div>
              <div className="font-semibold" style={{ fontSize: 13.5 }}>
                {uploadingDocuments.length === 1
                  ? "Uploading your file…"
                  : `Uploading ${uploadingDocuments.length.toLocaleString()} files…`}
              </div>
              <div className="text-muted-foreground" style={{ fontSize: 11.5 }}>
                Please wait while we upload and process your files.
              </div>
              <div
                role="progressbar"
                aria-label="Files uploading"
                aria-valuetext="Upload in progress"
                className="mt-2 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-muted"
              >
                <span
                  className="block h-full w-1/3 rounded-full bg-foreground"
                  style={{ animation: "databook-upload-progress 1.35s ease-in-out infinite" }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-center">
              <div
                className="rounded-full bg-card border border-border flex items-center justify-center"
                style={{ width: 44, height: 44 }}
              >
                <Plus size={20} className="text-muted-foreground" />
              </div>
              <div className="font-medium" style={{ fontSize: 13 }}>{uploadPrompt}</div>
              <div className="text-muted-foreground" style={{ fontSize: 11.5 }}>
                Excel, CSV, PDF, Word, PowerPoint and common archives.
              </div>
            </div>
          )}
        </label>

        {documentsLoadError && (
          <div
            className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive"
            style={{ fontSize: 11.5 }}
          >
            Existing project documents could not be loaded. Refresh the page to try again.
          </div>
        )}

        {documentsLoading && (
          <div
            className="mt-6 text-center text-muted-foreground"
            style={{ fontSize: 11.5 }}
          >
            Loading project documents…
          </div>
        )}

        {documents.length > 0 && (
          <>
            <div className="mt-7 mb-2 flex flex-wrap items-center gap-3">
              <div className="flex shrink-0 items-center gap-2" style={{ fontSize: 11.5 }}>
                <span className="font-semibold">Data room</span>
                <span className="text-muted-foreground">
                  {documents.length.toLocaleString()} file
                  {documents.length === 1 ? "" : "s"}
                  {totalSize > 0 && <> · {formatSize(totalSize)}</>}
                </span>
              </div>
              <div className="ml-auto flex min-w-[120px] flex-1 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:max-w-[240px] focus-within:ring-1 focus-within:ring-foreground/30">
                <Search size={12} className="shrink-0 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  aria-label="Search files"
                  placeholder="Search files…"
                  className="min-w-0 flex-1 outline-none bg-transparent"
                  style={{ fontSize: 12 }}
                />
              </div>
              {canClearDocuments && (
                <button
                  type="button"
                  onClick={onClearDocuments}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  style={{ fontSize: 10.5 }}
                >
                  Delete all
                </button>
              )}
            </div>

            {shownDocuments.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground text-xs italic">
                No files match.
              </div>
            )}
            {documentGroups.filter((group) => group.documents.length > 0).map((group) => (
              <div key={String(group.unsupported)} className={group.unsupported ? "mt-5" : undefined}>
                {group.unsupported && (
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-muted-foreground">Unsupported files</div>
                  </div>
                )}
                <div className="border border-border rounded-xl bg-card overflow-hidden">
                  <div className="grid grid-cols-[20px_minmax(0,1fr)_60px_80px] items-center gap-2 border-b border-border/40 bg-muted/30 px-3 py-1.5 text-[10.5px] text-muted-foreground sm:gap-3">
                    <span />
                    <span>Name</span>
                    <span className="text-right">Size</span>
                    <span className="sr-only">Actions</span>
                  </div>
                  <div
                    className="overflow-y-auto divide-y divide-border/40"
                    style={{ maxHeight: 460 }}
                  >
                    {group.documents.map((document) => {
                      const format = formatOf(document.name);
                      const icon = FORMAT_ICONS[format];
                      const Icon = format === "csv" ? FileSpreadsheet : FileText;
                      const statusLabel = document.isSupported === false && document.status === "ready"
                        ? "Unsupported file"
                        : documentStatusLabel(document.status);
                      return (
                        <div
                          key={document.key}
                          className={`grid grid-cols-[20px_minmax(0,1fr)_60px_80px] items-center gap-2 px-3 py-1.5 hover:bg-muted/40 transition-colors sm:gap-3 ${
                            document.status === "uploading" ? "bg-muted/20" : ""
                          }`}
                        >
                          {icon ? (
                            <img src={icon} alt="" className="h-5 w-5 object-contain" />
                          ) : (
                            <Icon size={20} className={format === "csv" ? "text-emerald-700" : "text-muted-foreground"} aria-hidden="true" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div
                              className="font-medium truncate"
                              style={{ fontSize: 12 }}
                              title={document.name}
                            >
                              {document.name}
                            </div>
                            {statusLabel && (
                              <div
                                className={
                                  document.status === "error"
                                    ? "text-destructive"
                                    : "text-muted-foreground"
                                }
                                style={{ fontSize: 10.5 }}
                              >
                                {document.status === "uploading" && (
                                  <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-foreground align-middle" />
                                )}
                                {statusLabel}
                              </div>
                            )}
                          </div>
                          <span className="text-right text-[10.5px] tabular-nums text-muted-foreground">
                            {formatSize(document.size)}
                          </span>
                          <div className="flex items-center justify-end gap-1">
                            {document.canRetry && onRetryDocument && (
                              <button
                                type="button"
                                onClick={() => onRetryDocument(document)}
                                className="text-muted-foreground hover:text-foreground shrink-0 p-1"
                                style={{ fontSize: 10.5 }}
                              >
                                Retry
                              </button>
                            )}
                            {document.canDownload && onDownloadDocument && (
                              <button
                                type="button"
                                aria-label={`Download ${document.name}`}
                                onClick={() => onDownloadDocument(document)}
                                className="text-muted-foreground hover:text-foreground shrink-0 p-1"
                              >
                                <Download size={12} />
                              </button>
                            )}
                            {canRemoveDocuments && (
                              <button
                                type="button"
                                aria-label={`Remove ${document.name}`}
                                disabled={
                                  document.status === "uploading"
                                  || document.status === "deleting"
                                }
                                onClick={() => onRemoveDocument(document)}
                                className="text-muted-foreground hover:text-destructive disabled:opacity-40 shrink-0 p-1"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            {shownDocuments.length < documents.length && (
              <div
                className="px-3 py-1.5 border-t border-border/60 text-muted-foreground bg-muted/30"
                style={{ fontSize: 10 }}
              >
                Showing {shownDocuments.length.toLocaleString()} of{" "}
                {documents.length.toLocaleString()} files
              </div>
            )}
          </>
        )}

        <style>{`
          @keyframes databook-upload-progress {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(420%); }
          }
        `}</style>

        {analysisRunning && (
          <div
            role="status"
            className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-border bg-card px-5 py-4"
          >
            <span
              aria-hidden="true"
              className="h-7 w-7 shrink-0 animate-spin rounded-full border-[3px] border-muted border-t-foreground motion-reduce:animate-none"
            />
            <div className="min-w-0 flex-1">
              <div className="font-semibold" style={{ fontSize: 12.5 }}>
                Analysing {analysisFileCount.toLocaleString()} {analysisFileCount === 1 ? "file" : "files"}
              </div>
              <p
                className="mt-1 text-muted-foreground"
                style={{ fontSize: 11.5, lineHeight: 1.5 }}
              >
                You can close the app now. This may take up to 30 minutes. We’ll notify you when it’s ready.
              </p>
            </div>
            <span
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-muted px-3 py-1 font-medium text-muted-foreground"
              style={{ fontSize: 10.5 }}
            >
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-foreground" />
              In progress
            </span>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          {onRetryAnalysis && (
            <button
              type="button"
              disabled={continueDisabled}
              onClick={onRetryAnalysis}
              className="rounded-lg border border-border bg-background px-4 py-2 font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
              style={{ fontSize: 12.5 }}
            >
              Retry analysis
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
